import { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useSpring,
  useMotionValue,
  useInView,
  animate,
} from "framer-motion";

export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.4 });
  return (
    <motion.div
      data-testid="scroll-progress-bar"
      className="fixed top-0 left-0 right-0 h-[2px] z-[100] origin-left"
      style={{
        scaleX,
        background: "linear-gradient(90deg, #D4AF37, #F5DE81)",
        boxShadow: "0 0 12px rgba(212,175,55,0.8)",
      }}
    />
  );
};

export const MouseGlow = () => {
  const x = useMotionValue(-400);
  const y = useMotionValue(-400);
  const sx = useSpring(x, { stiffness: 60, damping: 20 });
  const sy = useSpring(y, { stiffness: 60, damping: 20 });

  useEffect(() => {
    const move = (e) => {
      x.set(e.clientX - 300);
      y.set(e.clientY - 300);
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [x, y]);

  return (
    <motion.div
      aria-hidden
      className="pointer-events-none fixed z-[1] h-[600px] w-[600px] rounded-full hidden md:block"
      style={{
        x: sx,
        y: sy,
        background: "radial-gradient(circle, rgba(212,175,55,0.09) 0%, transparent 60%)",
      }}
    />
  );
};

export const Grain = () => <div aria-hidden className="grain-overlay" />;

export const Magnetic = ({ children, strength = 0.35, className = "" }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 14, mass: 0.2 });
  const sy = useSpring(y, { stiffness: 180, damping: 14, mass: 0.2 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: sx, y: sy, display: "inline-block" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const Reveal = ({ children, delay = 0, y = 36, className = "", once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const CountUp = ({ value, suffix = "", prefix = "", decimals = 0, duration = 2 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState("0");
  const [forced, setForced] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setForced(true), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!inView && !forced) return;
    const controls = animate(0, value, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v.toFixed(decimals)),
      onComplete: () => setDisplay(value.toFixed(decimals)),
    });
    return () => controls.stop();
  }, [inView, forced, value, decimals, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

export const SectionHead = ({ eyebrow, title, sub, align = "left", testId }) => (
  <div className={`mb-14 sm:mb-20 ${align === "center" ? "text-center" : ""}`}>
    <Reveal>
      <span
        data-testid={testId ? `${testId}-eyebrow` : undefined}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#F5DE81] font-medium"
      >
        <span className="h-px w-8 bg-[#D4AF37] inline-block" />
        {eyebrow}
      </span>
    </Reveal>
    <Reveal delay={0.08}>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.08] mt-5">
        {title}
      </h2>
    </Reveal>
    {sub && (
      <Reveal delay={0.16}>
        <p className={`text-sm sm:text-base text-slate-400 mt-5 max-w-xl leading-relaxed ${align === "center" ? "mx-auto" : ""}`}>
          {sub}
        </p>
      </Reveal>
    )}
  </div>
);
